import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { versionTotals } from '@app/shared/price-calc';
import { api, ApiError, fieldIssues } from '../api';
import { formatPln, parsePln } from '../catalog/money';
import { InvoiceLines } from './InvoiceLines';
import { dayText, INVOICES_KEY, type Invoice, type InvoiceLine } from './types';
import { DialogLayer } from '../ui/Dialog';
import { useUnsavedGuard } from '../ui/unsaved';

/**
 * "Korekta…" (F40): the reason (it goes to KSeF) and, optionally, a correction to zero — then the draft correction
 * opens with the invoice's lines "before" and a copy "after" to change.
 */
export function CorrectionDialog({ invoice, onClose }: { invoice: Invoice; onClose: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [toZero, setToZero] = useState(false);
  // an advance invoice is corrected by its amount (a refund), not by its lines
  const advance = invoice.kind === 'ZAL';
  const [amount, setAmount] = useState('');
  const [local, setLocal] = useState('');
  const create = useMutation({
    mutationFn: (advanceGrossGrosze?: number) => api<Invoice>(`/api/invoices/${invoice.id}/correction`, { method: 'POST', body: { reason: reason.trim(), ...(toZero ? { toZero } : {}), ...(advanceGrossGrosze !== undefined ? { advanceGrossGrosze } : {}) } }),
    onSuccess: (kor) => {
      void queryClient.invalidateQueries({ queryKey: INVOICES_KEY });
      navigate(`/finanse/faktury/${kor.id}`);
    },
  });
  const existing = create.error instanceof ApiError && create.error.code === 'draft_exists' ? (create.error.current as { id?: string } | undefined)?.id : undefined;
  /** The advance after the correction (only for an advance not to zero); null with the reason shown when wrong. */
  function advanceAmount(): number | undefined | null {
    if (!advance || toZero) return undefined;
    try {
      setLocal('');
      return parsePln(amount);
    } catch {
      setLocal('Wpisz kwotę zaliczki po korekcie (brutto), np. 2000,00');
      return null;
    }
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    if (reason.trim() === '') return;
    const gross = advanceAmount();
    if (gross !== null) create.mutate(gross);
  }
  // owner 2026-10-23 21:14: Escape with a reason typed and no correction made asks first
  const guardRef = useUnsavedGuard({
    dirty: reason.trim() !== '' || toZero || amount.trim() !== '',
    save: async () => {
      const gross = advanceAmount();
      if (reason.trim() === '' || gross === null) return false;
      await create.mutateAsync(gross);
      return true;
    },
  });
  const error = local || (create.error instanceof ApiError ? (fieldIssues(create.error)[0]?.message ?? create.error.message) : create.error ? 'Nie udało się utworzyć korekty' : null);
  return (
    <div className="dialog-backdrop">
      <form ref={guardRef} className="dialog panel inv-send" role="dialog" aria-modal="true" aria-labelledby="inv-kor-title" onSubmit={submit} data-feature-id="invoices.correction">
        <DialogLayer onClose={onClose} busy={create.isPending} />
        <h2 id="inv-kor-title">Korekta faktury {invoice.number}</h2>
        <p className="muted">Powstanie korekta robocza: pozycje faktury „przed korektą” i ich kopia „po korekcie” do zmiany. Wystawiona korekta trafia do KSeF.</p>
        {error && (
          <p className="alert" role="alert">
            {error} {existing && <Link to={`/finanse/faktury/${existing}`}>Otwórz korektę roboczą</Link>}
          </p>
        )}
        <label className="field">
          <span>Przyczyna korekty</span>
          <input value={reason} maxLength={256} onChange={(e) => setReason(e.target.value)} autoFocus placeholder="np. rabat po wydarzeniu, błędna cena" />
        </label>
        {advance && !toZero && (
          <label className="field">
            <span>Zaliczka po korekcie (brutto)</span>
            <input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={invoice.grossGrosze !== undefined ? `mniej niż ${formatPln(invoice.grossGrosze)}` : '0,00 zł'} />
          </label>
        )}
        <label className="check">
          <input type="checkbox" checked={toZero} onChange={(e) => setToZero(e.target.checked)} />
          <span>{advance ? 'Zwrot całej zaliczki (korekta do zera, np. wydarzenie odwołane)' : 'Korekta do zera – faktura przestaje obowiązywać (np. wydarzenie odwołane)'}</span>
        </label>
        <div className="actions">
          <button className="btn" type="button" onClick={onClose}>
            Anuluj
          </button>
          <button className="btn primary" type="submit" disabled={create.isPending || reason.trim() === ''}>
            {create.isPending ? 'Tworzenie…' : 'Utwórz korektę'}
          </button>
        </div>
      </form>
    </div>
  );
}

const sum = (lines: readonly InvoiceLine[], vatBp: number) => (lines.every((l) => l.netGrosze !== undefined) ? versionTotals(lines.map((l) => l.netGrosze!), vatBp) : null);

/**
 * The lines of a correction (KOR): what the corrected invoice says ("Przed korektą", read only) and what it should say
 * ("Po korekcie", edited in place in a draft; a line of the corrected invoice is set to 0 rather than removed), with
 * the totals before, after and the difference — "Do zwrotu" when the client gets money back.
 */
export function CorrectionLines({ invoice, editable, onSaved, onError }: { invoice: Invoice; editable: boolean; onSaved: (inv: Invoice) => void; onError: (e: unknown) => void }) {
  // a correction of an advance (KOR_ZAL): the advance before and after it, the order unchanged
  const advance = invoice.corrected?.kind === 'ZAL';
  const advBefore = advance ? invoice.advanceBefore[0]?.grossGrosze : undefined;
  const before = advance ? (advBefore === undefined ? null : { grossGrosze: advBefore }) : sum(invoice.linesBefore, invoice.vatBp);
  const after = advance ? (advBefore === undefined || invoice.grossGrosze === undefined ? null : { grossGrosze: advBefore + invoice.grossGrosze }) : sum(invoice.lines, invoice.vatBp);
  const c = invoice.corrected;
  const gross = invoice.grossGrosze;
  return (
    <section className="inv-kor" aria-label="Korekta" data-feature-id="invoices.correction">
      {c && (
        <p className="inv-kor-ref">
          Korekta faktury <Link to={`/finanse/faktury/${c.id}`}>{c.number}</Link> z dnia {dayText(c.issueDate)}
          {c.ksefNumber && <span className="muted"> · KSeF {c.ksefNumber}</span>}
          <br />
          Przyczyna: {invoice.correctionReason}
        </p>
      )}
      {advance ? (
        <>
          <h3 className="inv-kor-caption">Zamówienie (korekta zaliczki go nie zmienia)</h3>
          <InvoiceLines invoice={invoice} editable={false} label="Pozycje zamówienia" onSaved={onSaved} onError={onError} />
        </>
      ) : (
        <>
          <h3 className="inv-kor-caption">Przed korektą</h3>
          <InvoiceLines invoice={{ ...invoice, lines: invoice.linesBefore }} editable={false} label="Pozycje przed korektą" onSaved={onSaved} onError={onError} />
          <h3 className="inv-kor-caption">Po korekcie</h3>
          <InvoiceLines invoice={invoice} editable={editable} label="Pozycje po korekcie" keptIds={new Set(invoice.linesBefore.map((l) => l.id))} onSaved={onSaved} onError={onError} />
        </>
      )}
      {gross !== undefined && (
        <dl className="inv-totals">
          {before && (
            <div>
              <dt>{advance ? 'Zaliczka przed korektą (brutto)' : 'Przed korektą (brutto)'}</dt>
              <dd>{formatPln(before.grossGrosze)}</dd>
            </div>
          )}
          {after && (
            <div>
              <dt>{advance ? 'Zaliczka po korekcie (brutto)' : 'Po korekcie (brutto)'}</dt>
              <dd>{formatPln(after.grossGrosze)}</dd>
            </div>
          )}
          <div>
            <dt>Różnica netto / VAT</dt>
            <dd>
              {invoice.netGrosze === undefined ? '—' : formatPln(invoice.netGrosze)} / {invoice.vatGrosze === undefined ? '—' : formatPln(invoice.vatGrosze)}
            </dd>
          </div>
          <div className="inv-gross">
            <dt>{gross < 0 ? 'Do zwrotu' : 'Do zapłaty'}</dt>
            <dd data-testid="invoice-gross">{formatPln(Math.abs(gross))}</dd>
          </div>
        </dl>
      )}
    </section>
  );
}
